import React, {useEffect, useRef, useState} from 'react';
import * as yup from 'yup';
import {t} from 'react-switch-lang';
import {FormProvider, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers';
import {useParams} from 'react-router-dom';
import {Toast} from '../../../../../utils/sweetAlert';
import {Row, Col, Spinner} from 'react-bootstrap';
import {
  createExitStrategyForm,
  editExitStrategyForm, getExitStrategyForm,
} from '../../../../../services/externalizations/exitStrategyService';
import SelectList from "../../../../../components/FormFields/select/SelectList";
import InputText from '../../../../../components/FormFields/InputText';
import TextArea from '../../../../../components/FormFields/Textarea';
import SelectSearchMulti from '../../../../../components/select/SelectSearchMulti';
import {
  customStyles,
  exitStrategyTriggers,
  externalizationRequiredStepsOptions,
  handleCancelExternalization,
  insourcingTransitionAssessmentOptions,
  serviceContinuationOption,
  serviceProviderSwitchAssessmentOptions, statusesOfVerification,
  yesNoOptions,
} from '../../../../../utils/externalizationFormOptions';
import {useUserContext} from "../../../../../contexts/UserContext";
import FormActionButtons from '../../../components/formActionButtons/FormActionButtons';
import VerifyExternalizationModal from '../../../components/verifyExternalizationModal/VerifyExternalizationModal';
import {getProposalForm} from '../../../../../services/externalizations/proposalFormService';
import Swal from 'sweetalert2';
import {lang} from 'moment';
import {getDepartment} from "../../../../../services/IncurredEvent/incurredEventApi";
import {getExternalizationByCycleId} from "../../../../../services/externalizations/externalizationService";

const ExitStrategy = ({cycleId, isLastCycle, isEditAllowedByStatus, isVerified, externalizationFormConfigData}) => {
  const [disabled, setDisabled] = useState(false);
  const [exitStrategyFormId, setExitStrategyFormId] = useState();
  const [loading,setLoading] = useState(false);
  const [isReview,setIsReview] = useState(false);
  const [populated,setPopulated] = useState(false);
  const [showEditBtn,setShowEditBtn] = useState(false);
  const [defaultData,setDefaultData] = useState([])
  const [edit,setEdit] = useState(showEditBtn);
  const [implementationTeams, setImplementationTeams] = useState([]);
  const [implementationTeamsSend, setImplementationTeamsSend] = useState([]);
  const [implementationTeamsError, setImplementationTeamsError] = useState(false);
  const implementationTeamsFocus = useRef(null);
  const [departmentInChargeOnEdit, setDepartmentInChargeOnEdit] = useState()
  const user = useUserContext();
  const { id } = useParams();
  const [responsibleUserId, setResponsibleUserId] = useState()
  const [userInChargeId, setUserInChargeId] = useState()
  const [userInChargeDeputyId, setUserInChargeDeputyId] = useState()
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [verificationStatus, setVerificationStatus]= useState('');

  const canVerify = verificationStatus === 'PENDING'
  const userCanVerifyForm = user?.id === responsibleUserId;
  const isUserReviewer = user?.id === userInChargeId || user?.id === userInChargeDeputyId
  const toggleVerifyModal = () => setVerifyModalOpen(prev => !prev);
const schema = yup.object().shape({
  isServiceProviderRelatedToBank: yup.object().nullable().required(t('message.fieldRequired')),
  alternativeServiceProviders: yup
      .string()
      .trim()
      .required(t('message.fieldRequired'))
      .min(2, t('message.min', {min: 2}))
      .max(100, t('message.max', {max: 100})),
  detailedExplanationOfAlternativeServiceProviders: yup
      .string()
      .trim()
      .required(t('message.fieldRequired'))
      .min(2, t('message.min', {min: 2}))
      .max(30000, t('message.max', {max: 30000})),
  externalizationRiskMitigationClauses: yup
      .string()
      .trim()
      .required(t('message.fieldRequired'))
      .min(2, t('message.min', {min: 2}))
      .max(30000, t('message.max', {max: 30000})),
  exitStrategyGoals: yup
      .string()
      .trim()
      .required(t('message.fieldRequired'))
      .min(2, t('message.min', {min: 2}))
      .max(30000, t('message.max', {max: 30000})),
  exitStrategyTriggers: yup.array().nullable().required(t('message.fieldRequired')),
  externalizationArrangementControlIndicators: yup
      .string()
      .trim()
      .required(t('message.fieldRequired'))
      .min(2, t('message.min', {min: 2}))
      .max(30000, t('message.max', {max: 30000})),
  implementationTeams:  yup.array().nullable().required(t('message.fieldRequired')),
  exitStrategyTriggersDescription: yup
    .string()
    .trim()
    .when('exitStrategyTriggers', {
          is: (val) => Array.isArray(val) && val?.some(item => item?.value === 'OTHER'),
      then: yup
        .string()
        .required(t('message.fieldRequired'))
        .min(2, t('message.min', { min: 2 }))
        .max(30000, t('message.max', { max: 30000 })),
      otherwise: yup.string().nullable()
    }),
  serviceContinuationOptions: yup.array().nullable().required(t('message.fieldRequired')),
  financeResourceForExitStrategy: yup
      .string()
      .trim()
      .required(t('message.fieldRequired'))
      .min(2, t('message.min', {min: 2}))
      .max(30000, t('message.max', {max: 30000})),
  serviceContinuationOptionDescription: yup
    .string()
    .trim()
    .when('serviceContinuationOptions', {
      is: (val) => Array.isArray(val) && val?.some(item => item?.value === 'OTHER'),
      then: yup
        .string()
        .required(t('message.fieldRequired'))
        .min(2, t('message.min', { min: 2 }))
        .max(30000, t('message.max', { max: 30000 })),
      otherwise: yup.string().nullable()
    }),
  serviceProviderSwitchAssessment: yup.object().nullable().required(t('message.fieldRequired')),
  insourcingTransitionAssessment: yup.object().nullable().required(t('message.fieldRequired')),
  externalizationRequiredSteps: yup.array().nullable().required(t('message.fieldRequired')),
  externalizationRequiredStepsDescription: yup
    .string()
    .trim()
    .when('externalizationRequiredSteps', {
      is: (val) => Array.isArray(val) && val?.some(item => item?.value !== 'IMPLEMENTATION_TEAM'),
      then: yup
        .string()
        .required(t('message.fieldRequired'))
        .min(2, t('message.min', { min: 2 }))
        .max(30000, t('message.max', { max: 30000 })),
      otherwise: yup.string().nullable()
    }),
  monitoringResponsibleReview: yup
    .string()
    .trim()
    .required(t('message.fieldRequired'))
    .min(2, t('message.min', {min: 2}))
    .max(100, t('message.max', {max: 100})),
  summary: yup
      .string()
      .trim()
      .required(t('message.fieldRequired'))
      .min(2, t('message.min', {min: 2}))
      .max(30000, t('message.max', {max: 30000}))
});
  const methods = useForm({
    resolver: yupResolver(schema),
  });
  const {handleSubmit, setValue, errors, watch, register, reset} = methods;
  const watchExitStrategyTriggers = watch("exitStrategyTriggers")
  const watchServiceContinuationOption = watch("serviceContinuationOptions")
  const watchExternalizationRequiredSteps = watch("externalizationRequiredSteps")
  const watchServiceProviderSwitchAssessment = watch("serviceProviderSwitchAssessment");
  const [userInChargeForExternalizationName,setUserInChargeForExternalizationName] = useState();


  useEffect( () => {
        if(id !== null) {
          getExternalizationByCycleId(id).then((res) => {
            getDepartment(res.data.departmentId).then((res) => {
              setUserInChargeForExternalizationName(res.data[0].userInChargeForExternalizationFullName)
            })
          })
        }
  },[id])
  const checkDropdownTeams = (e) => {
    setImplementationTeams(e);
    setImplementationTeamsError(false);
    if(e === null ){
      setValue("implementationTeams", [])
    }
    if (e !== null && e?.length > 0) {
      errors.implementationTeams = undefined;
      const data = [];
      e.map(item => {
        data.push(
          {id: item?.value}
        )
      });
      setImplementationTeamsSend(data);
      setValue("implementationTeams", data)
    } else {
      errors.implementationTeams = {
        message: `${t("message.fieldRequired")}`,
        type: "required"
      };
      if (errors.implementationTeams) {
        const focusImplementationTeams = implementationTeamsFocus.current.select;
        focusImplementationTeams.focus()
      }
    }
  };

  const submitForm = async (data) => {
    implementationTeams !== null && implementationTeams.length === 0 && setImplementationTeamsError(true);
    setDisabled(true);
    const dataToSubmit = {
      ...(edit && {
        id: defaultData?.id ? defaultData?.id : exitStrategyFormId,
        departmentInCharge: defaultData?.id ? {id: defaultData?.departmentInCharge?.id} : {id: departmentInChargeOnEdit}
      }),
      isServiceProviderRelatedToBank: data?.isServiceProviderRelatedToBank?.value,
      alternativeServiceProviders: data?.alternativeServiceProviders,
      detailedExplanationOfAlternativeServiceProviders: data?.detailedExplanationOfAlternativeServiceProviders,
      externalizationRiskMitigationClauses: data?.externalizationRiskMitigationClauses,
      exitStrategyGoals: data?.exitStrategyGoals,
      exitStrategyTriggers: data?.exitStrategyTriggers?.map(item => item?.value),
      externalizationArrangementControlIndicators: data?.externalizationArrangementControlIndicators,
      exitStrategyTriggersDescription: data?.exitStrategyTriggersDescription,
      serviceContinuationOptions: data?.serviceContinuationOptions?.map(item => item?.value),
      financeResourceForExitStrategy: data?.financeResourceForExitStrategy,
      serviceContinuationOptionDescription: data?.serviceContinuationOptionDescription,
      serviceProviderSwitchAssessment: data?.serviceProviderSwitchAssessment?.value,
      insourcingTransitionAssessment: data?.insourcingTransitionAssessment?.value,
      externalizationRequiredSteps: data?.externalizationRequiredSteps?.map(item => item?.value),
      externalizationRequiredStepsDescription: data?.externalizationRequiredStepsDescription,
      populated: true,
      cycle: {id: parseInt(cycleId)},
      formConfig: externalizationFormConfigData,
      implementationTeams: implementationTeamsSend,
      monitoringResponsibleReview: data?.monitoringResponsibleReview,
      summary: data?.summary
    };
    if(edit) {
      try{
      const res = await editExitStrategyForm(defaultData?.id ? defaultData?.id : exitStrategyFormId, dataToSubmit)
        if (res?.status === 200) {
          Toast.fire({
            title: t('message.updateSuccess3', {
              entity: t('externalization.exitStrategyForm.exitStrategyForm'),
            }),
            icon: 'success',
          });
          const newVerificationStatus = res?.data?.verificationStatus;
          if (newVerificationStatus && newVerificationStatus !== verificationStatus) {
            setTimeout(() => {
              window.location.reload();
            }, 800);
          } else {
            setVerificationStatus(newVerificationStatus);
            setDisabled(true);
          }
        }
        setDisabled(true);
      }
      catch (err){
        if (err?.response?.data?.code === 'responsible-user-for-externalization-is-missing') {
          Swal.fire({
            text:
              lang === 'eng'
                ? err?.response?.data?.description
                : t('message.responsibleUserForExternalizationMissing'),
            icon: 'error',
          });
          setDisabled(false)
          setEdit(false)
        } else {
          Toast.fire({
            title: t('message.errorApi'),
            icon: 'error',
          });
        }
      }
    }else {
       createExitStrategyForm(dataToSubmit).then((res) => {
        if (res?.status === 201) {
          Toast.fire({
            title: t('message.addSuccess3', {
              entity: t('externalization.exitStrategyForm.exitStrategyForm'),
            }),
            icon: 'success',
          });
          setDisabled(true)
          setEdit(true);
          setIsReview(true);
          setVerificationStatus('PENDING')
          setVerificationStatus(res?.data?.verificationStatus)
          setResponsibleUserId(res?.data?.departmentInCharge?.userResponsibleForExternalizationId);
          setUserInChargeId(res?.data?.departmentInCharge?.userInChargeForExternalizationId);
          setUserInChargeDeputyId(res?.data?.departmentInCharge?.deputyUserInChargeForExternalizationId);
          setExitStrategyFormId(res?.data?.id)
          setDepartmentInChargeOnEdit(res?.data?.departmentInCharge?.id)
        }
      })
  }}
  useEffect(() => {
    async function fetchExitStrategyForm() {
      setLoading(true);
      try {
        const res = await getExitStrategyForm(cycleId);
        const form = res?.data?.[0] ?? null;
        if (form) {
          setDefaultData(form);
          setPopulated(!!form?.populated);
          setEdit(!!form?.populated);
          setDisabled(!!form?.populated);
          setVerificationStatus(form?.verificationStatus)
          const userId = user?.id;
          const deputyId = form?.departmentInCharge?.deputyUserInChargeForExternalizationId;
          const mainId = form?.departmentInCharge?.userInChargeForExternalizationId;
          const isUserReviewer = userId === deputyId || userId === mainId;
          setIsReview(!!form?.populated && isUserReviewer);
          setShowEditBtn(!form?.populated === false || !isUserReviewer);
          setResponsibleUserId(form?.departmentInCharge?.userResponsibleForExternalizationId)
          setUserInChargeId(form?.departmentInCharge?.userInChargeForExternalizationId)
          setUserInChargeDeputyId(form?.departmentInCharge?.deputyUserInChargeForExternalizationId)
        } else {
          const proposalRes = await getProposalForm(cycleId);
          const proposalFormData = proposalRes?.data?.[0] ?? null;
          const inChargeUserId = proposalFormData?.departmentInCharge?.userInChargeForExternalizationId;
          const deputyUserId = proposalFormData?.departmentInCharge?.deputyUserInChargeForExternalizationId;
          setUserInChargeId(inChargeUserId);
          setUserInChargeDeputyId(deputyUserId);
          const isReviewer = user?.id === inChargeUserId || user?.id === deputyUserId;
          setPopulated(false);
          setEdit(false);
          setDisabled(!isReviewer || !isLastCycle);
          setValue('processingDate', new Date());
        }
      } catch (err) {
        console.error('Error loading proposal form:', err);
        setPopulated(false);
      } finally {
        setLoading(false);
      }
    }
    fetchExitStrategyForm();

  }, [cycleId]);
  useEffect(() => {
    register({name: "implementationTeams", type: 'custom'}, {required: true});
  }, [register]);
  useEffect(() => {
    if (!watchServiceProviderSwitchAssessment?.value) return;
    const selectedValue = watchServiceProviderSwitchAssessment?.value;
    const matchingOption = insourcingTransitionAssessmentOptions.find(
      option => option?.value === selectedValue
    );
    if (matchingOption) {
      // setValue("insourcingTransitionAssessment", matchingOption);
    }
  }, [watchServiceProviderSwitchAssessment]);

  useEffect(() => {
    reset({
      exitStrategyTriggers: Array.isArray(defaultData?.exitStrategyTriggers)
        ? defaultData.exitStrategyTriggers?.map(r =>
          exitStrategyTriggers.find(item => item?.value === r)
        )
        : [],
      serviceContinuationOptions:  Array.isArray(defaultData?.serviceContinuationOptions)
        ? defaultData.serviceContinuationOptions?.map(r =>
          serviceContinuationOption.find(item => item?.value === r)
        )
        : [],
      externalizationRequiredSteps: Array.isArray(defaultData?.externalizationRequiredSteps)
        ? defaultData.externalizationRequiredSteps.map(r =>
          externalizationRequiredStepsOptions.find(item => item.value === r)
        )
        : [],
      implementationTeams: Array.isArray(defaultData?.implementationTeams) ?
        defaultData?.implementationTeams?.map((user) => ({
          value: user?.id,
          label: user?.fullName,
        })) : null
    })
    if (Array.isArray(defaultData?.implementationTeams)) {
      const mappedTeams = defaultData?.implementationTeams?.map((user) => ({
        value: user?.id,
        label: user?.fullName,
      }));
      setValue("implementationTeams", mappedTeams);
      setImplementationTeams(mappedTeams)
      setImplementationTeamsSend(mappedTeams?.map((r) => ({ id: r?.value })));
    }

  }, [defaultData, setValue]);

  if (loading) {
    return (
        <div className="text-center mt-4 mb-4">
          <Spinner animation="border" role="status" />
        </div>
    );
  }
  return (
    <>
      <FormActionButtons
        isReview={isReview}
        edit={edit}
        disabled={disabled}
        isLastCycle={isLastCycle}
        isEditAllowedByStatus={isEditAllowedByStatus}
        userCanVerifyForm={userCanVerifyForm}
        canVerify={canVerify}
        toggleVerifyModal={toggleVerifyModal}
        setDisabled={setDisabled}
      />
  <FormProvider {...methods}>
    <form onSubmit={handleSubmit(submitForm)}>
      <Row className="mb-2 w-100">
        <Col className="col-6 col-xl-3 mb-4">
          <label className="label w-100 mt-2">
        {t('externalization.exitStrategyForm.isServiceProviderRelatedToBank')}
      </label>
      <SelectList
        name="isServiceProviderRelatedToBank"
        className="select"
        options={yesNoOptions}
        placeholder={t('externalization.exitStrategyForm.placeholder.isServiceProviderRelatedToBank')}
        isClearable={false}
        errors={errors}
        defaultValue={
          typeof defaultData?.isServiceProviderRelatedToBank === 'boolean'
            ? yesNoOptions.find(item => item?.value === defaultData?.isServiceProviderRelatedToBank)
            : null
        }
        isDisabled={disabled}
      />
          <label className="label w-100 mt-2">
            {t('externalization.exitStrategyForm.alternativeServiceProviders')}
          </label>
          <InputText
            type="text"
            name="alternativeServiceProviders"
            className="form-control input-field"
            placeholder={t('externalization.exitStrategyForm.placeholder.alternativeServiceProviders')}
            disabled={disabled}
            defaultValue={defaultData?.alternativeServiceProviders}
          />
          <label className="label w-100 mt-2">
            {t('externalization.exitStrategyForm.detailsExplanationOfAlternativeServiceProviders')}
          </label>
          <TextArea
            name="detailedExplanationOfAlternativeServiceProviders"
            className={`form-control text-area `}
            placeholder={t('externalization.exitStrategyForm.placeholder.detailsExplanationOfAlternativeServiceProviders')}
            disabled={disabled}
            defaultValue={defaultData?.detailedExplanationOfAlternativeServiceProviders}
          />
          <label className="label w-100 mt-2">
            {t('externalization.exitStrategyForm.externalizationRiskMitigationClauses')}
          </label>
          <TextArea
            name="externalizationRiskMitigationClauses"
            className={`form-control text-area `}
            placeholder={t('externalization.exitStrategyForm.placeholder.externalizationRiskMitigationClauses')}
            disabled={disabled}
            defaultValue={defaultData?.externalizationRiskMitigationClauses}
          />
          <label className="label w-100 mt-2">
            {t('externalization.exitStrategyForm.exitStrategyGoals')}
          </label>
          <TextArea
              name="exitStrategyGoals"
              className={`form-control text-area `}
              placeholder={t('externalization.exitStrategyForm.placeholder.exitStrategyGoals')}
              disabled={disabled}
              defaultValue={defaultData?.exitStrategyGoals}
          />
        </Col>
        <Col className="col-6 col-xl-3">
          <label className="label w-100 mt-2">
            {t('externalization.exitStrategyForm.exitStrategyTriggers')}
          </label>
          <SelectList
            name="exitStrategyTriggers"
            className="select"
            options={exitStrategyTriggers}
            placeholder={t('externalization.exitStrategyForm.placeholder.exitStrategyTriggers')}
            isClearable={false}
            errors={errors}
            isDisabled={disabled}
            isMulti={true}
            value={watch("exitStrategyTriggers") ?? []}
            onChange={(values) => {
              setValue("exitStrategyTriggers", values, { shouldValidate: true });
            }}
          />
          {Array.isArray(watchExitStrategyTriggers) && watchExitStrategyTriggers?.some(item => item?.value === 'OTHER') &&
            <><br/>
              <TextArea
                name="exitStrategyTriggersDescription"
                className={`form-control text-area `}
                placeholder={t('externalization.exitStrategyForm.placeholder.specify')}
                disabled={disabled}
                defaultValue={defaultData?.exitStrategyTriggersDescription}
              /></>}
          <label className="label w-100 mt-2">
            {t('externalization.exitStrategyForm.exitStrategyIndicators')}
          </label>
          <TextArea
              name="externalizationArrangementControlIndicators"
              className={`form-control text-area`}
              placeholder={t('externalization.exitStrategyForm.placeholder.exitStrategyIndicators')}
              disabled={disabled}
              defaultValue={defaultData?.externalizationArrangementControlIndicators}
          />
          <label className="label w-100 mt-2">
            {t('externalization.exitStrategyForm.serviceContinuationOption')}
          </label>
          <SelectList
            name="serviceContinuationOptions"
            className="select"
            options={serviceContinuationOption}
            placeholder={t('externalization.exitStrategyForm.placeholder.serviceContinuationOption')}
            isClearable={false}
            errors={errors}
            isDisabled={disabled}
            isMulti={true}
            value={watch("serviceContinuationOptions") ?? []}
            onChange={(values) => {
              setValue("serviceContinuationOptions", values, { shouldValidate: true });
            }}
          />
            {Array.isArray(watchServiceContinuationOption) && watchServiceContinuationOption?.some(item => item?.value === 'OTHER') &&
            <><br/>
              <TextArea
                name="serviceContinuationOptionDescription"
                className={`form-control text-area `}
                placeholder={t('externalization.exitStrategyForm.placeholder.specify')}
                disabled={disabled}
                defaultValue={defaultData?.serviceContinuationOptionDescription}
              /></>}
          <label className="label w-100 mt-2">
            {t('externalization.exitStrategyForm.serviceProviderSwitchAssessment')}
          </label>
          <SelectList
            name="serviceProviderSwitchAssessment"
            className="select"
            options={serviceProviderSwitchAssessmentOptions}
            placeholder={t('externalization.exitStrategyForm.placeholder.serviceProviderSwitchAssessment')}
            isClearable={false}
            errors={errors}
            isDisabled={disabled}
            defaultValue={
              defaultData?.serviceProviderSwitchAssessment
                ? serviceProviderSwitchAssessmentOptions.find(item => item?.value === defaultData?.serviceProviderSwitchAssessment)
                : null
            }
          />
          <label className="label w-100 mt-2">
            {t('externalization.exitStrategyForm.financialResources')}
          </label>
          <TextArea
              name="financeResourceForExitStrategy"
              className={`form-control text-area `}
              placeholder={t('externalization.exitStrategyForm.placeholder.financialResources')}
              disabled={disabled}
              defaultValue={defaultData?.financeResourceForExitStrategy}
          />
        </Col>
        <Col className="col-6 col-xl-3 pb-4">
        <label className="label w-100 mt-2">
            {t('externalization.exitStrategyForm.externalizationRequiredSteps')}
          </label>
          <SelectList
            name="externalizationRequiredSteps"
            className="select"
            options={externalizationRequiredStepsOptions}
            placeholder={t('externalization.exitStrategyForm.placeholder.externalizationRequiredSteps')}
            isClearable={false}
            errors={errors}
            isMulti={true}
            isDisabled={disabled}
            value={watch("externalizationRequiredSteps") ?? []}
            onChange={(values) => {
              setValue("externalizationRequiredSteps", values, { shouldValidate: true });
            }}
          />
          {
            Array.isArray(watchExternalizationRequiredSteps) &&
            watchExternalizationRequiredSteps.filter(item => item && item?.value != null)?.length > 0 &&
            watchExternalizationRequiredSteps.some(item => item?.value !== 'IMPLEMENTATION_TEAM') &&
            <>
              <br/>
              <TextArea
                name="externalizationRequiredStepsDescription"
                className={`form-control text-area `}
                placeholder={t('externalization.exitStrategyForm.placeholder.specify')}
                disabled={disabled}
                defaultValue={defaultData?.externalizationRequiredStepsDescription}
              /></>}
          <label className="label w-100 mt-2">
            {t('externalization.exitStrategyForm.implementationTeams')}
          </label>
          <SelectSearchMulti
            api={`/users?active.equals=true`}
            name="implementationTeams"
            className={errors.implementationTeams && "error-field"}
            value={implementationTeams}
            onChange={checkDropdownTeams}
            placeholder={`${t('externalization.exitStrategyForm.placeholder.implementationTeams')}`}
            isMulti={true}
            ref={implementationTeamsFocus}
            optionLabel= "fullName"
            isSearchable={true}
            searchParams={['fullName.contains']}
            isDisabled={disabled}
            defaultValue={    (Array.isArray(defaultData?.implementationTeams)) ?
              defaultData?.implementationTeams?.map((user) => ({
              value: user?.id,
              label: user?.fullName,
            })) : null}
          />
          {errors.implementationTeams && (
            <span className="error-message">
                  {errors.implementationTeams.message}
              </span>
          )}
          <label className="label w-100 mt-2">
            {t('externalization.exitStrategyForm.monitoringResponsibleReview')}
          </label>
          <InputText
            type="text"
            name="monitoringResponsibleReview"
            className="form-control input-field"
            placeholder={t('externalization.exitStrategyForm.placeholder.monitoringResponsibleReview')}
            disabled={true}
            id="monitoringResponsibleReview"
            defaultValue={userInChargeForExternalizationName}
            // defaultValue={externalizationFormConfigData?.defaultDepartment.userInChargeForExternalizationFullName}
          />
          <label className='label w-100 mt-2'>
            {t('externalization.exitStrategyForm.insourcingTransitionAssessment')}
          </label>
          <SelectList
              name="insourcingTransitionAssessment"
              className="select"
              options={insourcingTransitionAssessmentOptions}
              isClearable={false}
              errors={errors}
              isDisabled={disabled}
              placeholder={t('externalization.exitStrategyForm.placeholder.insourcingTransitionAssessment')}
              showTooltip={true}
              style={customStyles}
              defaultValue={
                defaultData?.insourcingTransitionAssessment
                    ? insourcingTransitionAssessmentOptions.find(item => item?.value === defaultData?.insourcingTransitionAssessment)
                    : null
              }
          />
          <label className="label w-100 mt-2">
            {t('externalization.exitStrategyForm.conclusion')}
          </label>
          <TextArea
              name="summary"
              className={`form-control text-area `}
              placeholder={t('externalization.exitStrategyForm.placeholder.conclusion')}
              disabled={disabled}
              defaultValue={defaultData?.summary}
          />
          {edit && <>  <label className="label w-100 mt-2">{t('externalization.verificationStatus')}</label>
          <InputText
            name="verificationStatus"
            type="text"
            className="form-control input-field"
            placeholder={t( 'externalization.placeholder.verificationStatus')}
            disabled={true}
            defaultValue={statusesOfVerification.find(
              (status) => status?.value === verificationStatus)?.label}
          /></>}
        </Col>
      </Row>
      {(!edit || (edit && !disabled)) && isUserReviewer && isLastCycle && <Row className="mt-5 mb-3 form-footer">
        <button
          className="secondary-btn"
          type="reset"
          onClick={handleCancelExternalization}>
          {t('buttons.cancel')}
        </button>
        <button disabled={disabled} className="primary-btn">{t('buttons.save')}</button>
      </Row>}
    </form>
  </FormProvider>
      <VerifyExternalizationModal
        open={verifyModalOpen}
        formType={externalizationFormConfigData?.formKey}
        formId={defaultData?.id ? defaultData?.id : exitStrategyFormId}
        isOpen={setVerifyModalOpen}
      />
    </>
  );
};

export default ExitStrategy;